import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Paper,
  CircularProgress,
  Alert,
  alpha,
  useTheme,
  Chip,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import { useGetReportQuery } from '../../../store/api/reportsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { useGetProductsQuery, useGetCategoriesQuery } from '../../../store/api/productsApi';
import { getReportById } from '../config/reportCatalog';
import { exportReportToPdf, exportReportToExcel } from '../utils/reportExport';

function ReportView() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [productId, setProductId] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const [categoryId, setCategoryId] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // Reset when changing reports
  useEffect(() => {
    if (reportId === 'day-book' || reportId === 'daily-sales') {
      const today = new Date().toISOString().split('T')[0];
      setFromDate(today);
      setToDate(today);
    } else {
      const d = new Date();
      d.setDate(1);
      setFromDate(d.toISOString().split('T')[0]);
      setToDate(new Date().toISOString().split('T')[0]);
    }
    setProductId(null);
    setProductSearch('');
    setCategoryId('ALL');
    setStatusFilter('ALL');
  }, [reportId]);

  const { data: productsData, isFetching: isProductsLoading } = useGetProductsQuery(
    { search: productSearch, limit: 100 },
    { skip: reportId !== 'product-item-report' }
  );
  const products = productsData?.products || [];

  const { data: categoriesData, isFetching: isCategoriesLoading } = useGetCategoriesQuery(
    undefined,
    { skip: reportId !== 'category-wise-stock' && reportId !== 'product-sales-report' }
  );

  const categories = categoriesData ? [{ id: 'ALL', name: 'All Categories' }, ...categoriesData] : [{ id: 'ALL', name: 'All Categories' }];

  const reportMeta = getReportById(reportId);
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';

  const { data, isLoading, error } = useGetReportQuery(
    { reportType: reportId, fromDate, toDate, productId, categoryId: categoryId === 'ALL' ? undefined : categoryId, statusFilter },
    { skip: !reportId || (reportId === 'product-item-report' && !productId) }
  );

  const formatAmount = (value) => {
    const n = Number(value);
    if (Number.isNaN(n)) return '–';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency, minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2), maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2) }).format(n);
    } catch {
      return `${currency} ${n.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`;
    }
  };

  const formatDate = (v) => (v ? new Date(v).toLocaleDateString() : '–');

  const periodText = `${formatDate(fromDate)} – ${formatDate(toDate)}`;
  const safeFilename = (reportId || 'report').replace(/[^a-z0-9-_]/gi, '_');

  const handleExportPdf = () => {
    if (!data) return;
    exportReportToPdf({
      title: reportMeta?.label,
      companyName: company?.name,
      period: periodText,
      columns: data.columns || [],
      rows: data.rows || [],
      summaryItems: summaryItems.map((s) => ({ label: s.label, value: s.value })),
      filename: `${safeFilename}_${fromDate}_${toDate}`,
    });
  };

  const handleExportExcel = () => {
    if (!data) return;
    exportReportToExcel({
      title: reportMeta?.label,
      companyName: company?.name,
      period: periodText,
      columns: data.columns || [],
      rows: data.rows || [],
      summaryItems: summaryItems.map((s) => ({ label: s.label, value: s.value })),
      filename: `${safeFilename}_${fromDate}_${toDate}`,
      sheetName: (reportMeta?.label || 'Report').slice(0, 31),
    });
  };

  const handleBack = () => {
    // Prefer history back so we return to category page like /reports/category/inventory
    if (typeof window !== 'undefined' && window.history && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/reports');
  };

  if (!reportMeta) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Report not found.
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ borderRadius: 2 }}
        >
          Back to Reports
        </Button>
      </Box>
    );
  }

  const summaryItems = data?.summary
    ? [
      typeof data.summary.totalValue === 'number' && {
        label: 'Total Value',
        value: formatAmount(data.summary.totalValue),
      },
      typeof data.summary.totalSales === 'number' && {
        label: 'Total Sales',
        value: formatAmount(data.summary.totalSales),
      },
      typeof data.summary.totalCashSales === 'number' && {
        label: 'Cash Sales',
        value: formatAmount(data.summary.totalCashSales),
      },
      typeof data.summary.totalCreditSales === 'number' && {
        label: 'Credit Sales',
        value: formatAmount(data.summary.totalCreditSales),
      },
      typeof data.summary.receiptCash === 'number' && {
        label: 'Cash Receipts',
        value: formatAmount(data.summary.receiptCash),
      },
      typeof data.summary.receiptCard === 'number' && {
        label: 'Card Receipts',
        value: formatAmount(data.summary.receiptCard),
      },
      typeof data.summary.receiptBank === 'number' && {
        label: 'Bank Receipts',
        value: formatAmount(data.summary.receiptBank),
      },
      typeof data.summary.receiptCheque === 'number' && {
        label: 'Cheque Receipts',
        value: formatAmount(data.summary.receiptCheque),
      },
      typeof data.summary.totalPurchase === 'number' && {
        label: 'Total Purchase',
        value: formatAmount(data.summary.totalPurchase),
      },
      typeof data.summary.totalExpense === 'number' && {
        label: 'Total Expense',
        value: formatAmount(data.summary.totalExpense),
      },
      typeof data.summary.totalOutstanding === 'number' && {
        label: 'Outstanding',
        value: formatAmount(data.summary.totalOutstanding),
      },
      typeof data.summary.totalBalance === 'number' && {
        label: 'Total Balance',
        value: formatAmount(data.summary.totalBalance),
      },
      typeof data.summary.totalDebit === 'number' && {
        label: 'Total Debit',
        value: formatAmount(data.summary.totalDebit),
      },
      typeof data.summary.totalCredit === 'number' && {
        label: 'Total Credit',
        value: formatAmount(data.summary.totalCredit),
      },
      typeof data.summary.openingBalance === 'number' && {
        label: reportId === 'day-book' ? 'Opening Petty Cash' : 'Opening Balance',
        value: formatAmount(data.summary.openingBalance),
      },
      typeof data.summary.totalInflow === 'number' && reportId !== 'day-book' && {
        label: 'Total Inflow (Dr)',
        value: formatAmount(data.summary.totalInflow),
      },
      typeof data.summary.totalOutflow === 'number' && reportId !== 'day-book' && {
        label: 'Total Outflow (Cr)',
        value: formatAmount(data.summary.totalOutflow),
      },
      typeof data.summary.closingBalance === 'number' && {
        label: reportId === 'day-book' ? 'Closing Petty Cash' : 'Closing Balance',
        value: formatAmount(data.summary.closingBalance),
      },
      typeof data.summary.totalTaxable === 'number' && {
        label: 'Total Taxable',
        value: formatAmount(data.summary.totalTaxable),
      },
      typeof data.summary.totalInputVat === 'number' && {
        label: 'Total Input VAT',
        value: formatAmount(data.summary.totalInputVat),
      },
      typeof data.summary.totalOutputVat === 'number' && {
        label: 'Total Output VAT',
        value: formatAmount(data.summary.totalOutputVat),
      },
      typeof data.summary.totalReturns === 'number' && {
        label: 'Total Returns',
        value: formatAmount(data.summary.totalReturns),
      },
      typeof data.summary.totalNetPurchase === 'number' && {
        label: 'Net Purchase',
        value: formatAmount(data.summary.totalNetPurchase),
      },
      typeof data.summary.totalPaid === 'number' && {
        label: 'Total Paid',
        value: formatAmount(data.summary.totalPaid),
      },
      typeof data.summary.totalRevenue === 'number' && {
        label: 'Total Revenue',
        value: formatAmount(data.summary.totalRevenue),
      },
      typeof data.summary.totalCost === 'number' && {
        label: 'Total Cost',
        value: formatAmount(data.summary.totalCost),
      },
      typeof data.summary.totalProfit === 'number' && {
        label: 'Total Profit',
        value: formatAmount(data.summary.totalProfit),
      },
      typeof data.summary.netVat === 'number' && {
        label: 'Net VAT (Payable / Receivable)',
        value: formatAmount(data.summary.netVat),
      },
      typeof data.summary.totalDiscount === 'number' && {
        label: 'Total Discount',
        value: formatAmount(data.summary.totalDiscount),
      },
      data.summary.ordersWithDiscount != null && {
        label: 'Orders with Discount',
        value: data.summary.ordersWithDiscount.toLocaleString(),
      },
      data.summary.totalRows != null && {
        label: reportId === 'out-of-stock' ? 'Out of stock items' : 'Records',
        value: data.summary.totalRows.toLocaleString(),
      },
      data.summary.totalOrders != null && {
        label: 'Total Orders',
        value: data.summary.totalOrders.toLocaleString(),
      },
      data.summary.totalCategories != null && {
        label: 'Total Categories',
        value: data.summary.totalCategories.toLocaleString(),
      },
      data.summary.totalMatched != null && {
        label: 'Matched Products',
        value: data.summary.totalMatched.toLocaleString(),
      },
      data.summary.totalDiscrepancies != null && {
        label: 'Total Discrepancies',
        value: data.summary.totalDiscrepancies.toLocaleString(),
      },
      data.summary.totalPositive != null && {
        label: 'Positive Discrepancies',
        value: data.summary.totalPositive.toLocaleString(),
      },
      data.summary.totalNegative != null && {
        label: 'Negative Discrepancies',
        value: data.summary.totalNegative.toLocaleString(),
      },
      data.summary.totalMissing != null && {
        label: 'Missing Invoices',
        value: data.summary.totalMissing.toLocaleString(),
      },
      data.summary.slowMovingCount != null && {
        label: 'Slow Moving',
        value: data.summary.slowMovingCount.toLocaleString(),
      },
      data.summary.fastMovingCount != null && {
        label: 'Fast Moving',
        value: data.summary.fastMovingCount.toLocaleString(),
      },
      data.summary.totalQuantity != null && {
        label: 'Total Qty Sold',
        value: data.summary.totalQuantity.toLocaleString(),
      },
      data.summary.initialBalance != null && {
        label: 'Initial Balance',
        value: data.summary.initialBalance.toLocaleString(),
      },
      data.summary.totalSalesReturns != null && {
        label: 'Sales Returns',
        value: data.summary.totalSalesReturns.toLocaleString(),
      },
      data.summary.totalPurchaseReturns != null && {
        label: 'Purchase Returns',
        value: data.summary.totalPurchaseReturns.toLocaleString(),
      },
      data.summary.totalDamages != null && {
        label: 'Damages',
        value: data.summary.totalDamages.toLocaleString(),
      },
      data.summary.currentStock != null && {
        label: 'Current Total Stock',
        value: data.summary.currentStock.toLocaleString(),
      },
      data.summary.expectedStock != null && {
        label: 'Expected Stock (Calculated)',
        value: data.summary.expectedStock.toLocaleString(),
      },
      data.summary.discrepancy != null && {
        label: 'Discrepancy',
        value: data.summary.discrepancy.toLocaleString(),
      },
      data.summary.auditStatus != null && {
        label: 'Audit Status',
        value: data.summary.auditStatus,
      },
      typeof data.summary.totalClosing === 'number' && {
        label: 'Total Closing Stock',
        value: data.summary.totalClosing,
      },
      typeof data.summary.totalStock === 'number' && {
        label: 'Total Stock',
        value: data.summary.totalStock.toLocaleString(),
      },
    ].filter(Boolean)
    : [];

  return (
    <Box
      sx={{
        minHeight: '100%',
        bgcolor: alpha(theme.palette.primary.main, 0.02),
        borderRadius: 3,
        p: { xs: 2, sm: 3 },
        '@media print': { bgcolor: 'transparent', p: 0, '& .no-print': { display: 'none' } },
      }}
    >
      {/* Header */}
      <Box className="no-print" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              color: 'text.secondary',
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08), color: 'primary.main' },
            }}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em', color: 'text.primary' }}>
              {reportMeta.label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {company?.name}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Filters */}
      <Card
        className="no-print"
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <CalendarMonthOutlinedIcon sx={{ color: 'text.secondary', fontSize: 22 }} />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={reportId === 'day-book' ? "Date" : "From"}
                value={new Date(fromDate)}
                onChange={(newValue) => {
                  if (newValue && !isNaN(newValue.getTime())) {
                    const year = newValue.getFullYear();
                    const month = String(newValue.getMonth() + 1).padStart(2, '0');
                    const day = String(newValue.getDate()).padStart(2, '0');
                    const dateStr = `${year}-${month}-${day}`;
                    setFromDate(dateStr);
                    if (reportId === 'day-book') {
                      setToDate(dateStr);
                    }
                  }
                }}
                slotProps={{ textField: { size: 'small', sx: { width: 168 } } }}
                format="dd/MM/yyyy"
              />
              {reportId !== 'day-book' && (
                <DatePicker
                  label="To"
                  value={new Date(toDate)}
                  onChange={(newValue) => {
                    if (newValue && !isNaN(newValue.getTime())) {
                      const year = newValue.getFullYear();
                      const month = String(newValue.getMonth() + 1).padStart(2, '0');
                      const day = String(newValue.getDate()).padStart(2, '0');
                      const dateStr = `${year}-${month}-${day}`;
                      setToDate(dateStr);
                    }
                  }}
                  slotProps={{ textField: { size: 'small', sx: { width: 168 } } }}
                  format="dd/MM/yyyy"
                />
              )}
            </LocalizationProvider>
            {reportId === 'product-item-report' && (
              <Autocomplete
                size="small"
                options={products}
                loading={isProductsLoading}
                getOptionLabel={(option) => `${option.sku} - ${option.name}`}
                value={products.find(p => p.id === productId) || null}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input') {
                    // Basic debounce to avoid too many requests
                    clearTimeout(window.searchTimeout);
                    window.searchTimeout = setTimeout(() => {
                      setProductSearch(newInputValue);
                    }, 300);
                  }
                }}
                onChange={(event, newValue) => {
                  setProductId(newValue ? newValue.id : null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Product (SKU, Name, Brand...)"
                    sx={{ width: 350 }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isProductsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                filterOptions={(x) => x} // Disable client-side filtering since we do it on server
              />
            )}
            {(reportId === 'category-wise-stock' || reportId === 'product-sales-report') && (
              <Autocomplete
                size="small"
                options={categories}
                loading={isCategoriesLoading}
                getOptionLabel={(option) => option.name}
                value={categories.find(c => c.id === categoryId) || categories[0]}
                onChange={(event, newValue) => {
                  setCategoryId(newValue ? newValue.id : 'ALL');
                }}
                disableClearable
                id={'auto-complete-category'}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by Category"
                    sx={{ width: 250 }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isCategoriesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
            {reportId === 'stock-audit-report' && (
              <TextField
                select
                size="small"
                label="Audit Status Filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                SelectProps={{ native: true }}
                sx={{ width: 180 }}
              >
                <option value="ALL">All Statuses</option>
                <option value="DISCREPANCY">All Discrepancies</option>
                <option value="POSITIVE">Positive Discrepancy</option>
                <option value="NEGATIVE">Negative Discrepancy</option>
                <option value="MATCHED">Matched</option>
              </TextField>
            )}
            {reportId === 'stock-item-list' && (
              <TextField
                select
                size="small"
                label="Stock Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                SelectProps={{ native: true }}
                sx={{ width: 180 }}
              >
                <option value="ALL">All Items</option>
                <option value="INSTOCK">In Stock</option>
                <option value="OUTOFSTOCK">Out of Stock</option>
                <option value="NEGATIVE">Negative Stock</option>
              </TextField>
            )}
            {reportId === 'credit-sales' && (
              <TextField
                select
                size="small"
                label="Bill Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                SelectProps={{ native: true }}
                sx={{ width: 180 }}
              >
                <option value="ALL">All Bills</option>
                <option value="UNPAID">Unpaid Bills</option>
                <option value="PAID">Paid Bills</option>
              </TextField>
            )}
            <Button
              variant="contained"
              startIcon={<PictureAsPdfIcon />}
              onClick={handleExportPdf}
              disabled={!data}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
                boxShadow: 'none',
                '&:hover': { boxShadow: 1 },
              }}
            >
              Export PDF
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<TableChartIcon />}
              onClick={handleExportExcel}
              disabled={!data}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
                boxShadow: 'none',
                '&:hover': { boxShadow: 1 },
              }}
            >
              Export Excel
            </Button>
          </Box>
        </CardContent>
      </Card>

      {isLoading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Loading report…
          </Typography>
        </Box>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-message': { fontWeight: 500 },
          }}
        >
          {error?.data?.message || 'Failed to load report'}
        </Alert>
      )}

      {data && !isLoading && (
        <>
          {/* Report period (print) */}
          <Box sx={{ mb: 2, '@media print': { mb: 1 } }}>
            <Typography variant="body2" color="text.secondary">
              Period: {formatDate(fromDate)} – {formatDate(toDate)}
            </Typography>
          </Box>

          {/* Summary */}
          {summaryItems.length > 0 && (
            <Card
              elevation={0}
              sx={{
                mb: 3,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <SummarizeOutlinedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    Summary
                  </Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(auto-fill, minmax(220px, 1fr))' }, gap: 2 }}>
                  {summaryItems.map((item, index) => {
                    const isAudit = item.label === 'Audit Status';
                    const isDiscrepancy = item.label === 'Discrepancy';
                    const isClosing = item.label.toLowerCase().includes('closing');
                    const isOpening = item.label.toLowerCase().includes('opening');
                    const isSales = item.label.toLowerCase().includes('sales');
                    const isReceipt = item.label.toLowerCase().includes('receipt');

                    let statusColor = 'primary.main';
                    let bgColor = alpha(theme.palette.primary.main, 0.04);
                    let borderColor = alpha(theme.palette.primary.main, 0.1);

                    if (isAudit) {
                      statusColor = item.value === 'MATCHED' ? 'success.main' : 'error.main';
                      bgColor = alpha(theme.palette[item.value === 'MATCHED' ? 'success' : 'error'].main, 0.04);
                      borderColor = alpha(theme.palette[item.value === 'MATCHED' ? 'success' : 'error'].main, 0.1);
                    } else if (isDiscrepancy && Number(item.value) !== 0) {
                      statusColor = 'warning.main';
                      bgColor = alpha(theme.palette.warning.main, 0.04);
                      borderColor = alpha(theme.palette.warning.main, 0.1);
                    } else if (isClosing || isOpening) {
                       statusColor = 'secondary.main';
                       bgColor = alpha(theme.palette.secondary.main, 0.04);
                       borderColor = alpha(theme.palette.secondary.main, 0.1);
                    } else if (isSales || isReceipt) {
                       statusColor = 'success.main';
                       bgColor = alpha(theme.palette.success.main, 0.04);
                       borderColor = alpha(theme.palette.success.main, 0.1);
                    }

                    return (
                      <Box
                        key={`${item.label}-${index}`}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: bgColor,
                          border: '1px solid',
                          borderColor: borderColor,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                          }
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
                          {item.label}
                        </Typography>
                        <Typography variant="h6" sx={{ color: statusColor, fontWeight: 700, lineHeight: 1.2 }}>
                          {item.value}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          )}

          {data.message && (
            <Alert
              severity="info"
              sx={{ mb: 3, borderRadius: 2 }}
            >
              {data.message}
            </Alert>
          )}

          {/* Table */}
          {data.rows && data.rows.length > 0 && data.columns && data.columns.length > 0 && (() => {
            const normalizedColumns = data.columns.map((col, i) =>
              typeof col === 'string'
                ? { id: col, label: col }
                : { id: col.id ?? col.label ?? `col_${i}`, label: col.label ?? col.id ?? `Column ${i + 1}` }
            );

            const columnTotals = {};
            normalizedColumns.forEach((col, colIdx) => {
              const colLower = col.id.toLowerCase();
              const isNumeric = data.rows.some(row => typeof row[col.id] === 'number');
              
              if (isNumeric) {
                const sum = data.rows.reduce((acc, row) => {
                  const val = Number(row[col.id]);
                  return acc + (Number.isFinite(val) ? val : 0);
                }, 0);
                columnTotals[col.id] = sum;
              } else {
                columnTotals[col.id] = null;
              }
            });

            return (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'auto',
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {normalizedColumns.map((col) => (
                        <TableCell
                          key={col.id}
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            color: 'text.secondary',
                            bgcolor: alpha(theme.palette.grey[500], 0.08),
                            py: 1.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          {col.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.rows.map((row, idx) => (
                      <TableRow
                        key={idx}
                        hover
                        sx={{
                          bgcolor: idx % 2 === 1 ? alpha(theme.palette.grey[500], 0.03) : 'transparent',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                          },
                        }}
                      >
                        {normalizedColumns.map((col) => {
                          const raw = row[col.id];
                          let display = raw;
                          const colLower = col.id.toLowerCase();
                          const isQuantity =
                            typeof raw === 'number' &&
                            (colLower.includes('quantity') ||
                              colLower.includes('qty') ||
                              colLower === 'totalquantity' ||
                              colLower === 'quantitysold' ||
                              colLower === 'totalsold');
                          const isCurrency =
                            reportId !== 'product-item-report' &&
                            typeof raw === 'number' &&
                            !isQuantity &&
                            (colLower.includes('amount') ||
                              colLower.includes('value') ||
                              colLower.includes('revenue') ||
                              colLower.includes('cost') ||
                              colLower === 'profit' ||
                              colLower === 'debit' ||
                              colLower === 'credit' ||
                              colLower === 'balance' ||
                              colLower === 'subtotal' ||
                              (colLower.includes('total') && colLower !== 'totalrows'));
                          if (isCurrency) {
                            display = formatAmount(raw);
                          } else if (typeof raw === 'number' && (colLower.includes('margin') || colLower === 'vatrate')) {
                            display = `${Number(raw).toFixed(1)}%`;
                          } else if (
                            raw &&
                            (colLower.includes('date') || col.id === 'date')
                          ) {
                            display = formatDate(raw);
                          }
                          if (colLower === 'status' || colLower === 'auditstatus') {
                            let chipColor = 'default';
                            const valStr = String(raw).toUpperCase();
                            if (valStr.includes('MATCHED')) chipColor = 'success';
                            else if (valStr.includes('POSITIVE')) chipColor = 'info';
                            else if (valStr.includes('NEGATIVE')) chipColor = 'error';
                            else if (valStr.includes('DISCREPANCY')) chipColor = 'warning';
                            else chipColor = 'primary';
                            
                            return (
                              <TableCell key={col.id} sx={{ py: 1, borderColor: 'divider' }}>
                                {raw ? (
                                  <Chip label={String(raw)} size="small" color={chipColor} variant="filled" sx={{ fontWeight: 700, borderRadius: 1.5 }} />
                                ) : '–'}
                              </TableCell>
                            );
                          }

                          return (
                            <TableCell
                              key={col.id}
                              sx={{
                                py: 1.5,
                                fontWeight:
                                  isCurrency || colLower.includes('value') || colLower.includes('margin') ? 600 : 400,
                                borderColor: 'divider',
                              }}
                            >
                              {display != null && display !== '' ? String(display) : '–'}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}

                    {/* Total Row */}
                    <TableRow
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                        },
                      }}
                    >
                      {normalizedColumns.map((col, colIdx) => {
                        const totalVal = columnTotals[col.id];
                        const colLower = col.id.toLowerCase();
                        
                        let display = '–';
                        if (totalVal !== null) {
                          const isQuantity =
                            colLower.includes('quantity') ||
                            colLower.includes('qty') ||
                            colLower === 'totalquantity' ||
                            colLower === 'quantitysold' ||
                            colLower === 'totalsold';
                          
                          const isPercentage = colLower.includes('margin') || colLower === 'vatrate';

                          if (isQuantity) {
                            display = totalVal.toLocaleString();
                          } else if (isPercentage) {
                            display = '–';
                          } else {
                            display = formatAmount(totalVal);
                          }
                        } else if (colIdx === 0) {
                          display = 'Total';
                        }

                        return (
                          <TableCell
                            key={`total-${col.id}`}
                            sx={{
                              py: 1.5,
                              fontWeight: 700,
                              fontSize: '0.8rem',
                              color: 'text.primary',
                              borderTop: '2px solid',
                              borderColor: theme.palette.primary.main,
                              borderBottom: '2px solid',
                            }}
                          >
                            {display}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            );
          })()}

          {data.rows && data.rows.length === 0 && !data.message && (
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'divider',
                py: 6,
                textAlign: 'center',
              }}
            >
              <Typography variant="body1" color="text.secondary">
                No data for the selected period.
              </Typography>
            </Card>
          )}
        </>
      )}
    </Box>
  );
}

export default ReportView;
